import { APP_ZONES, BaseWidgetProperties } from '@finxone-platform/shared/sys-config-types';

/**
 * To remove hyphen, underscore from zone or page name.
 * @param paramName - zone or page name
 */
export function formatNameAsUrl(paramName: string | undefined): string {
  if (!paramName) {
    return '';
  }
  const words = paramName.split(/[-_ ]/);
  return words
    .map((word) => word.toLowerCase())
    .filter((word) => word !== 'zone')
    .join('-');
}

/**
 * Returns the matching APP_ZONES enum value for a given zone string.
 *
 * The input is normalized with `formatNameAsUrl`, so you can pass short forms
 * like `"profile"` and it will correctly match `"profile_zone"` from APP_ZONES.
 *
 * @param zone - A string representing the zone name (e.g. "profile", "payments").
 * @returns The matching APP_ZONES value (e.g. "profile_zone") or null if no match.
 *
 * @example
 * getMatchedZone("profile");   // → "profile_zone"
 * getMatchedZone("payments");  // → "payments_zone"
 * getMatchedZone("template");  // → "template_editor"
 * getMatchedZone("random");    // → null
 */
export function getMatchedZone(zone: string): APP_ZONES | null {
  if (!zone) return null;

  const formattedInput = formatNameAsUrl(zone);

  const match = Object.values(APP_ZONES).find((value) => formatNameAsUrl(value) === formattedInput);

  return match ?? null;
}

/**
 * Splits a zone URL into its zone and page parts.
 *
 * Expected format: `/zones/<zone>/<page>`
 *
 * @param url Full URL string.
 * @returns An object containing `zoneToNavigate` (APP_ZONES) and `urlToNavigate` (string),
 *          or the original URL if parsing fails.
 *
 * @example
 * getWidgetPropertiesUrlByPageUrl("/zones/profile/bank-statements-account-file-type");
 * // → {
 *        zoneToNavigate: APP_ZONES.PROFILE,
 *        urlToNavigate: "bank-statements-account-file-type",
 *        textContent: ""
 *   }
 *
 * getWidgetPropertiesUrlByPageUrl("/login");
 * // → {
      zoneToNavigate: null,
      urlToNavigate: undefined,
      textContent: ""
    }
 */
export function getWidgetPropertiesUrlByPageUrl(url: string): BaseWidgetProperties | null {
  try {
    // url can be /zones/profile/bank-statements-account-file-type
    const parts = url.split('/');

    // assuming the format is always /zones/<zone>/<pageUrl>
    const zoneToNavigate = getMatchedZone(parts[2]); // returns APP_ZONES value
    const urlToNavigate = parts[3]; // "bank-statements-account-file-type"

    return { zoneToNavigate, urlToNavigate, textContent: '' } as BaseWidgetProperties;
  } catch (error) {
    return null;
  }
}

// Helper to strip query/hash from pageUrl-like strings
export function stripQueryFromPageUrl(value?: string): string | undefined {
  if (!value) return value;
  // remove everything from first ? or # onward
  return value.split(/[?#)]/)[0];
}
